/**
 * Annual EcoVadis-style audit (CSR / RSE).
 *
 * Fires at the fiscal year-end IF Claire Maréchal is hired (the dashboard
 * unlock signal). Snapshots the current RSE score, archives it under
 * `ecoVadisHistory`, computes a year-over-year delta and a tier, and
 * pushes a celebratory or warning message into the Inbox.
 *
 * Pure-ish: takes the live state in, returns the audit payload + the
 * Messagerie payload. Caller persists.
 *
 * 01/06/2026 — QHSE / RSE module Phase 2 (Claire Maréchal perk).
 */
import { computeRseScore, TIER_COLORS } from './rseScore';

const TTL_DAYS = 30;

/**
 * @param {object} state           live game state.
 * @returns {object|null}          { audit, message } or null when conditions not met.
 */
export function buildAnnualEcoVadisAudit(state) {
  if (!state) return null;
  // Trigger condition: Claire is hired (signal = rse_dashboard unlock).
  const hasUnlock = !!(state.hiredSupport && state.hiredSupport['role-claire-marechal']);
  if (!hasUnlock) return null;

  const score = computeRseScore(state);
  if (!score) return null;

  const year = state.year || 1;
  const prevHistory = Array.isArray(state.ecoVadisHistory) ? state.ecoVadisHistory : [];
  const lastAudit = prevHistory[prevHistory.length - 1] || null;
  const delta = lastAudit ? score.overall - lastAudit.overall : 0;

  const tone = TIER_COLORS[score.tier] || TIER_COLORS.none;
  const tierLabel = tone.label;

  let body, msgTone;
  if (score.tier === 'gold') {
    msgTone = 'celebratory';
    body =
      `Patron,\n\n` +
      `**Audit EcoVadis ${year} : ${tierLabel} (score ${score.overall} / 100)**. ` +
      `Vous êtes officiellement dans le **top 10 %** des transporteurs européens en matière de RSE.\n\n` +
      `Conséquences concrètes :\n` +
      `   • **Réputation +3** (effet immédiat)\n` +
      `   • **Marchés publics & grands comptes RSE** débloqués (contrats premium +25 % à +30 % de marge)\n` +
      `   • Communication possible sur le badge **Gold** auprès de vos clients\n\n` +
      `Année prochaine, on vise le maintien — c'est plus dur que de l'obtenir une première fois.\n\n` +
      `— Claire Maréchal, Responsable QHSE / RSE`;
  } else if (score.tier === 'silver') {
    msgTone = 'celebratory';
    body =
      `Patron,\n\n` +
      `**Audit EcoVadis ${year} : ${tierLabel} (score ${score.overall} / 100)**. ` +
      `Niveau **engagé**, au-dessus de la moyenne sectorielle.\n\n` +
      `Conséquences :\n` +
      `   • **Réputation +1**\n` +
      `   • **Contrats RSE premium** débloqués (+15 % de marge moyenne)\n` +
      `   • Pour viser le Gold l'année prochaine : montez la part de flotte verte au-dessus de 30 %.\n\n` +
      `— Claire Maréchal`;
  } else if (score.tier === 'bronze') {
    msgTone = 'advice';
    body =
      `Patron,\n\n` +
      `**Audit EcoVadis ${year} : ${tierLabel} (score ${score.overall} / 100)**. ` +
      `Vous êtes au seuil — c'est une base, pas une fin.\n\n` +
      `Pour passer Silver l'année prochaine :\n` +
      `   • Score CO2 : ${score.sub.co2}/100${score.sub.co2 < 60 ? ' (faible — privilégier l\'électrique sur les renouvellements)' : ''}\n` +
      `   • Flotte verte : ${score.sub.green}/100${score.sub.green < 60 ? ' (objectif : 30 % de la flotte)' : ''}\n` +
      `   • Parité H/F : ${score.sub.parity}/100\n` +
      `   • Équité salariale : ${score.sub.wage}/100\n\n` +
      `— Claire Maréchal`;
  } else {
    msgTone = 'warning';
    body =
      `Patron,\n\n` +
      `**Audit EcoVadis ${year} : non noté (score ${score.overall} / 100)**.\n\n` +
      `Vous êtes en dessous du seuil bronze (35/100). Les contrats RSE premium restent fermés, ` +
      `et la marque souffre sur les appels d'offres publics.\n\n` +
      `Les 2 leviers prioritaires : **flotte verte** (${score.sub.green}/100) et **CO2/km** (${score.sub.co2}/100). ` +
      `Un seul véhicule électrique en plus, et un renouvellement diesel décalé, change la note.\n\n` +
      `— Claire Maréchal`;
  }

  const audit = {
    year,
    overall: score.overall,
    tier: score.tier,
    sub: score.sub,
    delta,
    auditedOnDay: state.totalDays || 0,
  };

  const message = {
    senderType: 'support',
    senderName: 'Claire Maréchal (QHSE)',
    senderRole: 'role-claire-marechal',
    tone: msgTone,
    kind: 'info',
    subject: `Audit EcoVadis ${year} — ${tierLabel} (${score.overall} / 100)`,
    body,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };

  return { audit, message };
}

/**
 * Returns the reputation bonus tied to an audit tier. Pure mapping.
 */
export function getEcoVadisReputationBonus(tier) {
  if (tier === 'gold') return 3;
  if (tier === 'silver') return 1;
  return 0;
}
